/**
 * Shared parsing utilities for message data
 * Consolidates duplicated parsing logic from across the codebase
 */

/**
 * Parse tool_calls from a message (handles string or array format)
 * @param {Object} message - Message object with tool_calls
 * @returns {Array} - Parsed tool calls array (empty array if none/invalid)
 */
export function parseToolCalls(message) {
  let toolCalls = message?.tool_calls || message?.metadata?.tool_calls;

  if (!toolCalls) return [];

  if (typeof toolCalls === "string") {
    try {
      toolCalls = JSON.parse(toolCalls);
    } catch {
      return [];
    }
  }

  return Array.isArray(toolCalls) ? toolCalls : [];
}

/**
 * Parse storage_files from message metadata
 * @param {Object} metadata - Message metadata object
 * @returns {Array} - Parsed files array (empty array if none/invalid)
 */
export function parseStorageFiles(metadata) {
  if (!metadata?.storage_files) return [];

  const storageFiles = metadata.storage_files;

  if (typeof storageFiles === "string") {
    try {
      const parsed = JSON.parse(storageFiles);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // Fallback to comma-separated string
      return storageFiles
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean);
    }
  }

  return Array.isArray(storageFiles) ? storageFiles : [];
}

/**
 * Check if tool calls array has any completed tool calls (with results)
 * @param {Array} toolCalls - Parsed tool calls array
 * @returns {boolean}
 */
export function hasCompletedToolCalls(toolCalls) {
  if (!Array.isArray(toolCalls) || toolCalls.length === 0) return false;
  return toolCalls.some((tc) => tc && tc.result);
}

/**
 * Find a tool call by name(s)
 * @param {Array} toolCalls - Parsed tool calls array
 * @param {string|string[]} toolNames - Tool name(s) to search for
 * @returns {Object|null} - Found tool call or null
 */
export function findToolCallByName(toolCalls, toolNames) {
  if (!Array.isArray(toolCalls)) return null;

  const names = Array.isArray(toolNames) ? toolNames : [toolNames];

  return (
    toolCalls.find((call) => {
      const toolName = call?.function?.name || call?.name;
      return names.includes(toolName);
    }) || null
  );
}

/**
 * Extract image tool call ID from tool calls
 * @param {Array} toolCalls - Parsed tool calls array
 * @returns {string|null} - Tool call ID or null
 */
export function getImageToolCallId(toolCalls) {
  if (!Array.isArray(toolCalls)) return null;

  // First try exact matches
  const imageToolCall = findToolCallByName(toolCalls, [
    "image_gen",
    "image_edit",
    "image_generation",
  ]);

  if (imageToolCall) {
    return imageToolCall.id || null;
  }

  // Fallback: check for tool names containing "image"
  const fallback = toolCalls.find((call) => {
    const toolName = call?.function?.name || call?.name;
    return toolName?.includes("image");
  });

  return fallback?.id || null;
}

/**
 * Get the tool name from a tool call object
 * @param {Object} toolCall - Tool call object
 * @returns {string|null} - Tool name or null
 */
export function getToolName(toolCall) {
  return toolCall?.function?.name || toolCall?.name || null;
}

/**
 * Get reasoning content from a message (checks multiple possible locations)
 * @param {Object} message - Message object
 * @returns {string|null} - Reasoning content or null
 */
export function getReasoningContent(message) {
  return (
    message?.metadata?.reasoningContent ||
    message?.reasoningContent ||
    message?.metadata?.reasoning_content ||
    null
  );
}
